import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { catchError, map, Observable } from 'rxjs';
import {
  CategoryId,
  ContractId,
  IConversationEditResponse,
  IDirectAssignmentContacts,
  IHQTicketDetailsResponse,
  IMetadataTicketDetailsResponse,
  IRootQuestionResponseTicketShortInput,
  IRootQuestionTicketOverview,
  ISort,
  ITicketConversation,
  ITicketConversationEdit,
  ITicketEdit,
  ITicketEditResponse,
  ITicketMetadataCreate,
  ITicketResidentCategory,
  ITicketResidentOverviewHQResponse,
  ITicketResidentOverviewResponse,
  TicketIssueType,
  TicketSortField,
  TicketStatus,
} from '../../models';
import {
  addMessageToTicketMutation,
  archiveTicketMutation,
  cancelTicketQuery,
  createTicketHQMutation,
  createTicketMetadataMutation,
  getCategoryDetailQuestionsQuery,
  getCategoryQuery,
  getDirectAssignmentsQuery,
  getTicketCategoryQuestionsQuery,
  getTicketQuestionsQuery,
  ticketChatQuery,
  ticketDetailHQQuery,
  ticketDetailMetadataQuery,
  ticketsOverviewQuery,
  ticketsOverviewQueryHQ,
} from '../../queries';

@Injectable()
export class TicketsFacade {
  constructor(private apollo: Apollo) {}

  public getTicketsOverview(
    contractNumbers: string[],
    issueTypes: TicketIssueType[],
    archived: boolean,
    offset: number,
    limit: number,
    sort?: ISort<TicketSortField>
  ): Observable<ITicketResidentOverviewResponse> {
    return this.apollo
      .query<{ ticketsOverviewV3: ITicketResidentOverviewResponse }>({
        query: ticketsOverviewQuery,
        variables: {
          input: {
            issueTypes,
            archived,
            contractNumbers,
            ticketOverviewSortField: sort?.value,
            sortOrder: sort?.direction,
          },
          offset,
          limit,
        },
      })
      .pipe(map(response => response?.data?.ticketsOverviewV3));
  }

  public getTicketsOverviewHQ(
    contractNumber: string,
    issueType: TicketIssueType,
    ticketStatus: TicketStatus,
    offset: number,
    limit: number,
    sort?: ISort<TicketSortField>
  ): Observable<ITicketResidentOverviewHQResponse> {
    return this.apollo
      .query<{ ticketsOverview: ITicketResidentOverviewHQResponse }>({
        query: ticketsOverviewQueryHQ,
        variables: {
          input: {
            issueType,
            ticketStatus,
            contractNumber,
            ticketOverviewSortField: sort?.value,
            sortOrder: sort?.direction,
          },
          offset,
          limit,
        },
      })
      .pipe(map(response => response?.data?.ticketsOverview));
  }

  public getCategoryQuestions(
    issueType: TicketIssueType,
    contractNumber: string
  ): Observable<IRootQuestionTicketOverview> {
    return this.apollo
      .query<{ ticketCategoryQuestions: IRootQuestionTicketOverview }>({
        query: getTicketCategoryQuestionsQuery,
        variables: { issueType, contractNumber },
      })
      .pipe(
        map(response => response?.data?.ticketCategoryQuestions),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public getTicketQuestions(
    request: IRootQuestionResponseTicketShortInput,
    issueType: TicketIssueType,
    contractNumber: string
  ): Observable<IRootQuestionTicketOverview> {
    return this.apollo
      .query<{ ticketQuestions: IRootQuestionTicketOverview }>({
        query: getTicketQuestionsQuery,
        variables: {
          categoryResponses: {
            rootQuestionId: request.rootQuestionId,
            responses: request.responses,
          },
          issueType,
          contractNumber,
        },
      })
      .pipe(
        map(response => response?.data?.ticketQuestions),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public getDetailedQuestions(
    request: IRootQuestionResponseTicketShortInput,
    issueType: TicketIssueType,
    contractNumber: string
  ): Observable<ITicketResidentCategory> {
    return this.apollo
      .query<{ getCategory: ITicketResidentCategory }>({
        query: getCategoryQuery,
        variables: {
          categoryResponses: {
            rootQuestionId: request.rootQuestionId,
            responses: request.responses,
          },
          issueType,
          contractNumber,
        },
      })
      .pipe(map(response => response?.data?.getCategory));
  }

  public getTicketDetailsHQ(
    ticketId: string,
    ticketIssueType: TicketIssueType
  ): Observable<IHQTicketDetailsResponse> {
    return this.apollo
      .query<{ ticketDetail: IHQTicketDetailsResponse }>({
        query: ticketDetailHQQuery,
        variables: { ticketId, ticketIssueType },
      })
      .pipe(
        map(response => {
          if (response?.data?.ticketDetail) {
            return response.data.ticketDetail;
          }
          throw new Error('Ticket detail response missing');
        })
      );
  }

  public createTicketHQ(input: ITicketEdit): Observable<ITicketEditResponse> {
    return this.apollo
      .mutate<{ createTicket: ITicketEditResponse }>({
        mutation: createTicketHQMutation,
        variables: { input },
      })
      .pipe(
        map(response => {
          // errorPolicy 'all' delivers GraphQL / downstream errors in response.error without
          // throwing; guard against a populated ticket returned alongside an error. See ART-19008.
          if (response.error) {
            throw response.error;
          }
          if (response?.data?.createTicket) {
            return response.data.createTicket;
          }
          throw new Error('Create ticket response missing');
        })
      );
  }

  public cancelTicket(ticketId: string, issueType: TicketIssueType): Observable<boolean> {
    return this.apollo
      .mutate<{ cancelTicket: boolean }>({
        mutation: cancelTicketQuery,
        variables: { input: { ticketId }, issueType },
      })
      .pipe(
        map(response => {
          /**
           * TODO
           * BE always sends cancelTicket: null
           * change this when they fix that
           * https://immomio.atlassian.net/browse/ART-5575
           */
          if (
            response?.data &&
            Object.prototype.hasOwnProperty.call(response.data, 'cancelTicket')
          ) {
            return response.data.cancelTicket;
          }
          throw new Error('Cancel ticket response missing');
        })
      );
  }

  public archiveTicket(ticketId: string): Observable<boolean> {
    return this.apollo
      .mutate<{ archiveTicket: boolean }>({
        mutation: archiveTicketMutation,
        variables: { input: { ticketId } },
      })
      .pipe(
        map(response => {
          if (response?.data?.archiveTicket) {
            return response.data.archiveTicket;
          }
          throw new Error('Archive ticket response missing');
        })
      );
  }

  public getTicketChat(ticketId: string): Observable<ITicketConversation> {
    return this.apollo
      .query<{ getTicketConversationV2: ITicketConversation }>({
        query: ticketChatQuery,
        variables: { ticketId },
      })
      .pipe(
        map(response => {
          if (response?.data?.getTicketConversationV2) {
            return response.data.getTicketConversationV2;
          }
          throw new Error('Ticket conversation response missing');
        })
      );
  }

  public addMessageToTicket(input: ITicketConversationEdit): Observable<IConversationEditResponse> {
    return this.apollo
      .mutate<{ addMessageToTicketConversationV2: IConversationEditResponse }>({
        mutation: addMessageToTicketMutation,
        variables: {
          input,
        },
      })
      .pipe(
        map(result => {
          if (result?.data?.addMessageToTicketConversationV2) {
            return result.data.addMessageToTicketConversationV2;
          }
          throw new Error('Add message to ticket response missing');
        })
      );
  }

  public getTicketDetailsMetadata(
    ticketId: string,
    ticketIssueType: TicketIssueType
  ): Observable<IMetadataTicketDetailsResponse> {
    return this.apollo
      .query<{ metadataTicketDetail: IMetadataTicketDetailsResponse }>({
        query: ticketDetailMetadataQuery,
        variables: { ticketId, ticketIssueType },
      })
      .pipe(
        map(response => {
          if (response?.data?.metadataTicketDetail) {
            return response.data.metadataTicketDetail;
          }
          throw new Error('Metadata ticket detail response missing');
        })
      );
  }

  public createTicketMetadata(input: ITicketMetadataCreate): Observable<ITicketEditResponse> {
    return this.apollo
      .mutate<{ createMetadataTicket: ITicketEditResponse }>({
        mutation: createTicketMetadataMutation,
        variables: { input },
      })
      .pipe(
        map(response => {
          // errorPolicy 'all' delivers GraphQL / downstream errors in response.error without
          // throwing; the BE always returns createMetadataTicket: null (even on success), so
          // response.error is the only reliable success/failure signal. See ART-19008.
          if (response.error) {
            throw response.error;
          }
          /**
           * TODO
           * BE always sends createMetadataTicket: null
           * change this when they fix that
           */
          if (
            response?.data &&
            Object.prototype.hasOwnProperty.call(response.data, 'createMetadataTicket')
          ) {
            return response.data.createMetadataTicket;
          }
          throw new Error('Create metadata ticket response missing');
        })
      );
  }

  public getDirectAssignments(
    categoryId: CategoryId,
    contractId: ContractId
  ): Observable<IDirectAssignmentContacts> {
    return this.apollo
      .query<{ getDirectAssignments: IDirectAssignmentContacts }>({
        query: getDirectAssignmentsQuery,
        variables: { contractId, categoryId },
      })
      .pipe(
        map(response => {
          if (response?.data?.getDirectAssignments) {
            return response.data.getDirectAssignments;
          }
          throw new Error('Direct assignments response missing');
        })
      );
  }

  public getCategoryDetailQuestions(
    ticketCategoryId: string
  ): Observable<IRootQuestionTicketOverview> {
    return this.apollo
      .query<{ getCategoryDetailQuestions: IRootQuestionTicketOverview }>({
        query: getCategoryDetailQuestionsQuery,
        variables: { ticketCategoryId },
      })
      .pipe(
        map(response => {
          return response?.data?.getCategoryDetailQuestions;
        }),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }
}
